import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  AnimatePresence,
  motion,
  useMotionValueEvent,
  useReducedMotion,
  useScroll,
  useTransform,
} from "motion/react";
import { ArrowUpRight } from "@phosphor-icons/react";
import { EASE_OUT, MaskWords, PageFrame, ScrubStatement, Seo, useMediaQuery } from "../../kit.jsx";
import { aarohCollaborators } from "../../data/content.js";
import { AAROH_ALBUM, AAROH_CLIPS, AAROH_REEL } from "../../data/album.js";
import { Btn, Count, GoLink, Rise, clamp01 } from "./pgkit.jsx";
import { AlbumImg, ClipVideo } from "./media.jsx";
import AarohGallery from "./AarohGallery.jsx";
import "./programmes.css";

const ALBUM = "https://photos.app.goo.gl/ipECCr2Dz9jkjKfM9";
const SITE = "https://www.aarohindia.com/";

const photo = (id) => AAROH_ALBUM.find((item) => item.id === id);
const clip = (id) => AAROH_CLIPS.find((item) => item.id === id);

const REEL_CLIP = { ...AAROH_REEL, alt: "A short silent reel from AAROH: the hall at work, teams at laptops, speakers and awards." };

const NUMBERS = [
  [363, "", "young innovators from across India"],
  [2, " days", "1 and 2 August 2026, at IIIT Delhi"],
  [4, "", "innovation tracks"],
  [13, "", "top teams recognised"],
  [15, "", "the age of the youngest participants"],
];

const TRACKS = [
  ["AI / ML", null],
  ["Game development", photo("M201")],
  ["Cybersecurity", photo("M185")],
  ["Hardware", photo("M192")],
];

/* Pinned essay: chapters built from album categories. One frame per row. */
const CHAPTERS = [
  { chapter: "The room", media: photo("M010"), title: "Before the doors opened.", body: "IIIT Delhi's main auditorium, set up for two days of building." },
  { chapter: "The room", media: photo("M054"), title: "Then it filled up.", body: "363 young innovators from across India, working in teams." },
  { chapter: "The opening", media: photo("M087"), title: "Think. Build. Launch.", body: "The opening talk, under the festival's line on the main screen: ideas that rise." },
  { chapter: "The build", media: clip("M029"), title: "Ideas argued out at the laptop.", body: "Teams worked through their ideas with peers and mentors, across four tracks." },
  { chapter: "The build", media: photo("M102"), title: "Paper first, then the screen.", body: "Planning in a notebook before building on screen." },
  { chapter: "The mic", media: photo("M682"), title: "The view from the lectern.", body: "Each team could explain a problem, present a solution and be heard by the whole hall." },
  { chapter: "The mic", media: photo("M167"), title: "Questions came from the floor.", body: "Young innovators met academic leaders, industry partners, mentors and one another." },
  { chapter: "Recognition", media: photo("M609"), title: "13 teams were recognised.", body: "Top teams were recognised on stage for their solutions and performance." },
  { chapter: "The people", media: photo("M533"), title: "The people who made it.", body: "School students led most of AAROH's organising and delivery." },
];

const STRIP = [clip("M005"), clip("M074"), clip("M069")];

const SUPPORTERS = [
  ["/images/partners/supporters/stacks.png", "Stacks KB"],
  ["/images/partners/supporters/fotocart.png", "Fotocart"],
  ["/images/partners/supporters/xyz.png", ".xyz"],
  ["/images/partners/supporters/github.png", "GitHub"],
  ["/images/partners/supporters/gradcircle.webp", "GradCircle"],
  ["/images/partners/supporters/skills4u.webp", "Skills4U"],
];

const isClip = (media) => media.src.endsWith(".mp4");

function Hero() {
  const ref = useRef(null);
  const reduceMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const scale = useTransform(scrollYProgress, (value) => 1 + clamp01(value) * 0.08);
  return (
    <section className="pg-ah" ref={ref} aria-labelledby="pg-ah-title">
      <div className="pg-ah__copy">
        <Rise as="p" className="gh-mono pg-kicker" onMount>AAROH 2026 · IIIT Delhi · 1–2 August 2026</Rise>
        <MaskWords as="h1" id="pg-ah-title" text="363 minds. One AAROH." accent="363" onMount delay={0.05} />
        <div className="pg-ah__row">
          <Rise as="p" onMount delay={0.35}>
            Two days at IIIT Delhi, on 1 and 2 August 2026. Young innovators from across India, some as young as 15. A festival led and run mainly by school students.
          </Rise>
          <Rise className="gh-actions" onMount delay={0.45}>
            <Btn href="#aaroh-numbers">See what happened</Btn>
            <Btn href="#aaroh-photos" variant="ghost-dark">Browse 70 photos</Btn>
          </Rise>
        </div>
      </div>
      <motion.div
        className="pg-ah__photo"
        initial={reduceMotion ? false : { clipPath: "inset(12% 6% 0% 6% round 28px)" }}
        animate={{ clipPath: "inset(0% 0% 0% 0% round 28px)" }}
        transition={{ duration: 1.4, delay: 0.3, ease: EASE_OUT }}
      >
        <motion.div className="pg-ah__zoom" style={reduceMotion ? undefined : { scale }}>
          <ClipVideo clip={REEL_CLIP} eager />
        </motion.div>
      </motion.div>
      <p className="pg-ah__caption">A 15-second silent reel from the two days at IIIT Delhi.</p>
    </section>
  );
}

function Numbers() {
  return (
    <section className="pg-anum" id="aaroh-numbers" aria-label="AAROH in numbers">
      <ul>
        {NUMBERS.map(([value, suffix, label], index) => (
          <Rise as="li" key={label} delay={index * 0.04}>
            <strong><Count to={value} suffix={suffix} /></strong>
            <span className="pg-anum__label">{label}</span>
          </Rise>
        ))}
      </ul>
    </section>
  );
}

function Tracks() {
  const reduceMotion = useReducedMotion();
  return (
    <section className="pg-atracks" aria-labelledby="pg-atracks-title">
      <div className="pg-atracks__head">
        <MaskWords id="pg-atracks-title" text="Four ways to build." />
        <p>Every team picked one of four tracks. The festival followed GRID&apos;s approach of Think → Build → Launch: understand a problem, build a first version, then present it.</p>
      </div>
      <ol className="pg-atracks__list">
        {TRACKS.map(([track, banner], index) => (
          <motion.li
            key={track}
            initial={reduceMotion ? false : { opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.6 }}
            transition={{ duration: 0.9, delay: index * 0.08, ease: EASE_OUT }}
          >
            <span className="gh-mono">Track {index + 1}</span>
            <strong>{track}</strong>
            {banner && (
              <span className="pg-atracks__thumb">
                <AlbumImg photo={banner} size="small" />
              </span>
            )}
          </motion.li>
        ))}
      </ol>
      <p className="pg-atracks__note">Three of the track banners, photographed in the building.</p>
    </section>
  );
}

function Media({ media, sizes = "(max-width: 899px) 100vw, 62vw" }) {
  return isClip(media) ? <ClipVideo clip={media} /> : <AlbumImg photo={media} sizes={sizes} />;
}

function EssayFrame({ media, index, count, progress }) {
  const t = useTransform(progress, (value) => (index === 0 ? 1 : clamp01((value * count - index + 0.4) / 0.4)));
  const clipPath = useTransform(t, (value) => `inset(${(1 - value) * 100}% 0% 0% 0%)`);
  const scale = useTransform(t, (value) => 1.14 - value * 0.14);
  return (
    <motion.figure className="pg-essay__frame" style={{ clipPath, zIndex: index + 1 }}>
      <motion.div className="pg-essay__zoom" style={{ scale }}>
        <Media media={media} />
      </motion.div>
    </motion.figure>
  );
}

function PinnedEssay() {
  const ref = useRef(null);
  const [active, setActive] = useState(0);
  const count = CHAPTERS.length;
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  useMotionValueEvent(scrollYProgress, "change", (value) => {
    const next = Math.min(count - 1, Math.max(0, Math.floor(value * count + 0.2)));
    setActive((previous) => (previous === next ? previous : next));
  });
  const current = CHAPTERS[active];
  const chapters = [...new Set(CHAPTERS.map((item) => item.chapter))];
  return (
    <div className="pg-essay is-pinned" ref={ref} style={{ height: `${count * 70 + 30}vh` }}>
      <div className="pg-essay__sticky">
        <div className="pg-essay__text">
          <ol className="pg-essay__chapters" aria-hidden="true">
            {chapters.map((name) => (
              <li key={name} className={name === current.chapter ? "is-active" : undefined}>{name}</li>
            ))}
          </ol>
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={active}
              aria-hidden="true"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.45, ease: EASE_OUT }}
            >
              <h3>{current.title}</h3>
              <p>{current.body}</p>
              <small>{current.media.caption}</small>
            </motion.div>
          </AnimatePresence>
          <span className="gh-mono pg-essay__index" aria-hidden="true">{String(active + 1).padStart(2, "0")} / {String(count).padStart(2, "0")}</span>
          <ol className="sr-only">
            {CHAPTERS.map((item) => <li key={item.media.id}>{item.chapter}: {item.title} {item.body}</li>)}
          </ol>
        </div>
        <div className="pg-essay__stage">
          {CHAPTERS.map((item, index) => (
            <EssayFrame key={item.media.id} media={item.media} index={index} count={count} progress={scrollYProgress} />
          ))}
        </div>
      </div>
    </div>
  );
}

function StackedEssay() {
  const reduceMotion = useReducedMotion();
  return (
    <ol className="pg-essay pg-essay--stacked">
      {CHAPTERS.map((item) => (
        <li key={item.media.id}>
          <span className="gh-mono">{item.chapter}</span>
          <motion.div
            className="pg-essay__card"
            initial={reduceMotion ? false : { clipPath: "inset(14% 0% 0% 0% round 20px)" }}
            whileInView={{ clipPath: "inset(0% 0% 0% 0% round 20px)" }}
            viewport={{ once: true, amount: 0.25 }}
            transition={{ duration: 1, ease: EASE_OUT }}
          >
            <Media media={item.media} sizes="100vw" />
          </motion.div>
          <h3>{item.title}</h3>
          <p>{item.body}</p>
        </li>
      ))}
    </ol>
  );
}

function PhotoEssay() {
  const reduceMotion = useReducedMotion();
  const wide = useMediaQuery("(min-width: 900px)");
  return (
    <section className="pg-aessay" aria-labelledby="pg-aessay-title">
      <header className="pg-aessay__head">
        <MaskWords id="pg-aessay-title" text="How the two days went." />
        <p>Six chapters, from the empty hall to the awards, in photographs and one clip from the event album.</p>
      </header>
      {wide && !reduceMotion ? <PinnedEssay /> : <StackedEssay />}
    </section>
  );
}

function Flashlights() {
  const ref = useRef(null);
  const reduceMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "start 0.1"] });
  const clipPath = useTransform(scrollYProgress, (value) => {
    const t = clamp01(value);
    return `inset(0% ${(1 - t) * 6}% 0% ${(1 - t) * 6}% round ${28 - t * 28}px)`;
  });
  const flash = clip("M129");
  return (
    <section className="pg-flash" ref={ref} aria-labelledby="pg-flash-title">
      <motion.div className="pg-flash__frame" style={reduceMotion ? undefined : { clipPath }}>
        <ClipVideo clip={flash} />
        <div className="pg-flash__copy">
          <span className="gh-mono">AAROH · IIIT Delhi</span>
          <h2 id="pg-flash-title">Lights off, phone torches up.</h2>
        </div>
      </motion.div>
      <p className="pg-flash__caption">{flash.caption} From the event video.</p>
    </section>
  );
}

function MotionStrip() {
  return (
    <section className="pg-strip" aria-labelledby="pg-strip-title">
      <header className="pg-strip__head">
        <h2 id="pg-strip-title">Between the photos.</h2>
        <p>Short silent clips from the event. Two play at a time, whichever are most in view.</p>
      </header>
      <ul>
        {STRIP.map((item, index) => (
          <Rise as="li" key={item.id} delay={index * 0.08} className={item.height > item.width ? "is-tall" : "is-wide"}>
            <span className="pg-strip__media"><ClipVideo clip={item} /></span>
            <p>{item.caption}</p>
          </Rise>
        ))}
      </ul>
    </section>
  );
}

function Backers() {
  const speaker = clip("M443");
  return (
    <section className="pg-abackers" aria-labelledby="pg-abackers-title">
      <MaskWords id="pg-abackers-title" text="People who backed young ideas." />
      <div className="pg-abackers__people">
        <Rise as="article">
          <span className="gh-mono">Recognition</span>
          <h3>Maj Gen (Retd.) P. K. Sehgal</h3>
          <p>Felicitated the top teams and spoke with the young innovators at the festival.</p>
        </Rise>
        <Rise as="article" delay={0.08}>
          <span className="gh-mono">Academic support</span>
          <h3>Prof. Anuj Grover, IIIT Delhi</h3>
          <p>Supported and encouraged participants, and connected the festival with the institute&apos;s academic and innovation community.</p>
        </Rise>
      </div>
      <div className="pg-abackers__media">
        <figure className="pg-abackers__clip">
          <span><ClipVideo clip={speaker} /></span>
          <figcaption>{speaker.caption}</figcaption>
        </figure>
        <figure className="pg-abackers__photo">
          <span><AlbumImg photo={photo("M655")} sizes="(max-width: 899px) 100vw, 40vw" /></span>
          <figcaption>{photo("M655").caption}</figcaption>
        </figure>
      </div>
    </section>
  );
}

function Collaborators() {
  const banner = photo("M400");
  return (
    <section className="pg-acollab" aria-labelledby="pg-acollab-title">
      <header className="pg-acollab__head">
        <MaskWords id="pg-acollab-title" text="Built together." />
        <p>Project GRID organised AAROH with Tachyon India and the Centre for Intelligent Product Development (CiPD) at IIIT Delhi.</p>
      </header>
      <ul className="pg-acollab__list">
        {aarohCollaborators.map((item, index) => {
          const body = (
            <>
              <span className="pg-acollab__logos">
                {item.logos.map((logo) => <img key={logo} src={logo} alt="" loading="lazy" decoding="async" />)}
              </span>
              <span className="pg-acollab__text">
                <span className="gh-mono">{item.role}</span>
                <strong>{item.name}</strong>
                <span>{item.description}</span>
              </span>
              <ArrowUpRight className="pg-acollab__arrow" weight="bold" aria-hidden="true" />
            </>
          );
          return (
            <Rise as="li" key={item.name} delay={index * 0.06}>
              {item.internal ? (
                <Link to={item.website}>{body}</Link>
              ) : (
                <a href={item.website} target="_blank" rel="noreferrer">{body}</a>
              )}
            </Rise>
          );
        })}
      </ul>
      <div className="pg-asupport">
        <div>
          <strong>Supporters</strong>
          <span>Organisations that supported AAROH.</span>
          <figure className="pg-asupport__banner">
            <span><AlbumImg photo={banner} size="small" /></span>
            <figcaption>{banner.caption}</figcaption>
          </figure>
        </div>
        <ul>
          {SUPPORTERS.map(([src, name], index) => (
            <Rise as="li" key={name} delay={index * 0.04}>
              <span className="pg-asupport__logo"><img src={src} alt="" loading="lazy" decoding="async" /></span>
              <span>{name}</span>
            </Rise>
          ))}
        </ul>
      </div>
    </section>
  );
}

function Finale() {
  const closing = photo("M098");
  return (
    <section className="pg-afinal" aria-labelledby="pg-afinal-title">
      <div className="pg-afinal__grid">
        <figure className="pg-afinal__photo">
          <span><AlbumImg photo={closing} sizes="(max-width: 899px) 100vw, 50vw" /></span>
          <figcaption>{closing.caption}</figcaption>
        </figure>
        <div className="pg-afinal__copy">
          <MaskWords id="pg-afinal-title" text="See every photo." />
          <ul className="pg-afinal__links">
            <li><a href={ALBUM} target="_blank" rel="noreferrer"><span>The full AAROH photo album</span><small>Google Photos</small><ArrowUpRight weight="bold" aria-hidden="true" /></a></li>
            <li><a href={SITE} target="_blank" rel="noreferrer"><span>aarohindia.com</span><small>The official AAROH site</small><ArrowUpRight weight="bold" aria-hidden="true" /></a></li>
            <li><Link to="/get-involved"><span>Help the next idea get built</span><small>Join as a student, mentor, volunteer or partner</small><ArrowUpRight weight="bold" aria-hidden="true" /></Link></li>
          </ul>
          <p className="pg-afinal__source"><GoLink to="/programmes">All programmes</GoLink></p>
        </div>
      </div>
    </section>
  );
}

export default function AarohPage() {
  return (
    <PageFrame className="rd-page pg-page pg-aaroh-page">
      <Seo
        title="AAROH 2026 impact: 363 innovators at IIIT-Delhi"
        path="/programmes/aaroh"
        description="AAROH brought 363 young innovators to IIIT-Delhi for two days of student-led innovation. Four tracks, 13 recognised teams and a community built around Think, Build, Launch."
      />
      <main className="pg-aaroh" data-nav-tone="dark">
        <Hero />
        <Numbers />
        <section className="pg-astatement" aria-label="Why AAROH worked">
          <ScrubStatement text="School students ran most of AAROH. They brought participants, academic leaders, industry partners and mentors into one place, so young people could build, trade ideas and be recognised for their work." />
        </section>
        <Tracks />
        <PhotoEssay />
        <Flashlights />
        <div id="aaroh-photos" className="pg-anchor">
          <AarohGallery album={ALBUM} />
        </div>
        <MotionStrip />
        <Backers />
        <Collaborators />
        <Finale />
      </main>
    </PageFrame>
  );
}
